import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import useToast from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';
import { notifyCompaniesUpdated } from '../utils/companyEvents';

const slugify = (value = '') =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  return new Date(value).toLocaleDateString('tr-TR');
};

const EMPTY_COMPANY_FORM = {
  name: '',
  slug: '',
  isActive: true
};

function ModalShell({ title, subtitle, isOpen, onClose, children, footer }) {
  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose}></div>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content directory-modal">
            <div className="modal-header border-0 pb-0">
              <div>
                <h5 className="modal-title">{title}</h5>
                {subtitle ? <p className="text-muted mb-0">{subtitle}</p> : null}
              </div>
              <button type="button" className="btn-close" onClick={onClose} aria-label="Kapat"></button>
            </div>
            <div className="modal-body">{children}</div>
            {footer ? <div className="modal-footer border-0 pt-0 d-flex gap-2 justify-content-end">{footer}</div> : null}
          </div>
        </div>
      </div>
    </>
  );
}

function CompanyManagementPage() {
  const { toasts, removeToast, showSuccess, showError } = useToast();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);

  const [companyModalMode, setCompanyModalMode] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState(EMPTY_COMPANY_FORM);
  const [slugTouched, setSlugTouched] = useState(false);

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignTargetCompany, setAssignTargetCompany] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getCompanies();
      if (response.success) {
        setCompanies(response.companies || []);
      }
    } catch (error) {
      console.error('Firma listesi alinamadi:', error);
      showError(error.message || 'Firma listesi yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const filteredCompanies = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return companies.filter((company) => {
      if (statusFilter === 'active' && !company.isActive) {
        return false;
      }

      if (statusFilter === 'inactive' && company.isActive) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      return (
        company.name?.toLowerCase().includes(normalizedSearch) ||
        company.slug?.toLowerCase().includes(normalizedSearch)
      );
    });
  }, [companies, searchTerm, statusFilter]);

  const summary = useMemo(
    () => ({
      total: companies.length,
      active: companies.filter((company) => company.isActive).length,
      inactive: companies.filter((company) => !company.isActive).length
    }),
    [companies]
  );

  const openCreateModal = () => {
    setCompanyModalMode('create');
    setEditingCompany(null);
    setCompanyForm(EMPTY_COMPANY_FORM);
    setSlugTouched(false);
  };

  const openEditModal = (company) => {
    setCompanyModalMode('edit');
    setEditingCompany(company);
    setCompanyForm({
      name: company.name || '',
      slug: company.slug || '',
      isActive: company.isActive !== false
    });
    setSlugTouched(true);
  };

  const closeCompanyModal = () => {
    setCompanyModalMode(null);
    setEditingCompany(null);
    setCompanyForm(EMPTY_COMPANY_FORM);
    setSlugTouched(false);
  };

  const handleCompanyFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    const nextValue = type === 'checkbox' ? checked : value;

    setCompanyForm((prev) => {
      const next = { ...prev, [name]: nextValue };

      if (name === 'name' && !slugTouched) {
        next.slug = slugify(nextValue);
      }

      return next;
    });
  };

  const handleSlugChange = (event) => {
    setSlugTouched(true);
    setCompanyForm((prev) => ({ ...prev, slug: slugify(event.target.value) }));
  };

  const handleSaveCompany = async (event) => {
    event.preventDefault();

    if (!companyForm.name.trim()) {
      showError('Firma adı zorunludur.');
      return;
    }

    const payload = {
      name: companyForm.name.trim(),
      slug: companyForm.slug.trim() || slugify(companyForm.name),
      isActive: companyForm.isActive
    };

    try {
      setSaving(true);

      if (companyModalMode === 'create') {
        const response = await api.createCompany(payload);
        showSuccess(response.message || 'Firma oluşturuldu.');
      } else if (editingCompany?._id) {
        const response = await api.updateCompany(editingCompany._id, payload);
        showSuccess(response.message || 'Firma güncellendi.');
      }

      closeCompanyModal();
      await loadCompanies();
      notifyCompaniesUpdated();
    } catch (error) {
      console.error('Firma kaydetme hatasi:', error);
      showError(error.message || 'Firma kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivateCompany = async (company) => {
    if (!company?.isActive) {
      return;
    }

    const confirmed = window.confirm(
      `"${company.name}" firmasını pasifleştirmek istediğinize emin misiniz?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setSaving(true);
      const response = await api.deleteCompany(company._id);
      showSuccess(response.message || 'Firma pasifleştirildi.');

      if (localStorage.getItem('activeCompanyId') === company._id) {
        localStorage.removeItem('activeCompanyId');
        localStorage.removeItem('activeCompanyName');
      }

      await loadCompanies();
      notifyCompaniesUpdated();
    } catch (error) {
      console.error('Firma pasiflestirme hatasi:', error);
      showError(error.message || 'Firma pasifleştirilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const openAssignModal = (company) => {
    setAssignTargetCompany(company);
    setAssignModalOpen(true);
    setUserSearch('');
    setUserResults([]);
    setSelectedUser(null);
  };

  const closeAssignModal = () => {
    setAssignModalOpen(false);
    setAssignTargetCompany(null);
    setUserSearch('');
    setUserResults([]);
    setSelectedUser(null);
  };

  const searchUsers = async () => {
    const query = userSearch.trim();

    if (query.length < 2) {
      showError('Arama için en az 2 karakter girin.');
      return;
    }

    try {
      setUserSearchLoading(true);
      const response = await api.getUsers({ search: query, limit: 8, status: 'active' });

      if (response.success) {
        const users = response.users || [];
        setUserResults(users);
        setSelectedUser(null);

        if (!users.length) {
          showError('Eşleşen kullanıcı bulunamadı.');
        }
      }
    } catch (error) {
      console.error('Kullanici arama hatasi:', error);
      showError(error.message || 'Kullanıcı araması başarısız.');
    } finally {
      setUserSearchLoading(false);
    }
  };

  const handleAssignAdmin = async () => {
    if (!assignTargetCompany?._id || !selectedUser?._id) {
      showError('Lütfen bir kullanıcı seçin.');
      return;
    }

    const confirmed = window.confirm(
      `${selectedUser.firstName} ${selectedUser.lastName} kullanıcısını "${assignTargetCompany.name}" firmasının yöneticisi yapmak istiyor musunuz?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setSaving(true);
      const response = await api.assignCompanyAdmin(assignTargetCompany._id, selectedUser._id);
      showSuccess(response.message || 'Firma yöneticisi atandı.');
      closeAssignModal();
    } catch (error) {
      console.error('Admin atama hatasi:', error);
      showError(error.message || 'Firma yöneticisi atanamadı.');
    } finally {
      setSaving(false);
    }
  };

  const companyModalFooter = (
    <>
      <button type="button" className="modern-hr-btn modern-hr-btn-secondary" onClick={closeCompanyModal} disabled={saving}>
        İptal
      </button>
      <button type="submit" form="company-form" className="modern-hr-btn modern-hr-btn-primary" disabled={saving}>
        {saving ? 'Kaydediliyor...' : companyModalMode === 'create' ? 'Oluştur' : 'Kaydet'}
      </button>
    </>
  );

  const assignModalFooter = (
    <>
      <button type="button" className="modern-hr-btn modern-hr-btn-secondary" onClick={closeAssignModal} disabled={saving}>
        İptal
      </button>
      <button type="button" className="modern-hr-btn modern-hr-btn-primary" onClick={handleAssignAdmin} disabled={saving || !selectedUser}>
        {saving ? 'Atanıyor...' : 'Yöneticiyi Ata'}
      </button>
    </>
  );

  return (
    <div className="modern-hr-page">
      <div className="container-fluid">
        <div className="modern-hr-header">
            <div className="modern-hr-header-content">
            <div className="modern-hr-header-text">
              <h1>Firma Yönetimi</h1>
              <p>Süper admin: firmaları oluşturun, düzenleyin ve firma yöneticisi atayın</p>
            </div>
            <div className="directory-admin-summary">
              <span>{summary.total} firma</span>
              <span>{summary.active} aktif</span>
              <span>{summary.inactive} pasif</span>
            </div>
          </div>
          <button type="button" className="modern-hr-btn modern-hr-btn-primary" onClick={openCreateModal}>
            <i className="bx bx-plus me-1"></i>
            Yeni Firma
          </button>
        </div>

        <div className="modern-hr-widget">
          <div className="modern-hr-widget-header">
            <div className="modern-hr-widget-title">
              <i className="bx bx-filter-alt"></i>
              <h4>Filtreler</h4>
            </div>
          </div>
          <div className="modern-hr-filters">
            <div className="row">
              <div className="col-md-8">
                  <div className="modern-hr-form-group">
                  <label className="modern-hr-form-label">Arama</label>
                  <div className="modern-hr-search-input">
                    <i className="bx bx-search"></i>
                    <input
                      className="modern-hr-form-input"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      placeholder="Firma adı veya slug"
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="modern-hr-form-group">
                  <label className="modern-hr-form-label">Durum</label>
                  <select
                    className="modern-hr-form-select"
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                  >
                    <option value="all">Tümü</option>
                    <option value="active">Aktif</option>
                    <option value="inactive">Pasif</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="modern-hr-widget">
          <div className="modern-hr-widget-header">
            <div className="modern-hr-widget-title">
              <i className="bx bxs-buildings"></i>
              <h4>Firma listesi</h4>
            </div>
          </div>

          {loading ? (
            <div className="modern-hr-loading">
              <i className="bx bx-loader-alt bx-spin"></i>
              <p>Firmalar yükleniyor...</p>
            </div>
          ) : filteredCompanies.length === 0 ? (
            <div className="modern-hr-empty">
              <i className="bx bxs-buildings"></i>
              <h5>Firma bulunamadı</h5>
              <p>Seçilen filtrelere uygun kayıt yok.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th>Firma</th>
                    <th>Slug</th>
                    <th>Durum</th>
                    <th>Oluşturulma</th>
                    <th className="text-end">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCompanies.map((company) => (
                    <tr key={company._id}>
                      <td className="fw-semibold">{company.name}</td>
                      <td>
                        <code>{company.slug}</code>
                      </td>
                      <td>
                        <span
                          className={`modern-employee-badge ${
                            company.isActive ? 'modern-employee-badge-active' : 'modern-employee-badge-inactive'
                          }`}
                        >
                          {company.isActive ? 'Aktif' : 'Pasif'}
                        </span>
                      </td>
                      <td>{formatDate(company.createdAt)}</td>
                      <td className="text-end">
                        <div className="d-flex justify-content-end gap-2 flex-wrap">
                          <button
                            type="button"
                            className="modern-hr-btn modern-hr-btn-secondary btn-sm"
                            onClick={() => openEditModal(company)}
                          >
                            Düzenle
                          </button>
                          <button
                            type="button"
                            className="modern-hr-btn modern-hr-btn-secondary btn-sm"
                            onClick={() => openAssignModal(company)}
                            disabled={!company.isActive}
                          >
                            Admin Ata
                          </button>
                          <button
                            type="button"
                            className="modern-hr-btn modern-hr-btn-secondary btn-sm text-danger"
                            onClick={() => handleDeactivateCompany(company)}
                            disabled={!company.isActive || saving}
                          >
                            Pasifleştir
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ModalShell
        title={companyModalMode === 'create' ? 'Yeni Firma' : 'Firmayı Düzenle'}
        subtitle="Firma adı ve slug benzersiz olmalıdır"
        isOpen={Boolean(companyModalMode)}
        onClose={closeCompanyModal}
        footer={companyModalFooter}
      >
        <form id="company-form" onSubmit={handleSaveCompany}>
          <div className="modern-hr-form-group mb-3">
            <label className="modern-hr-form-label" htmlFor="company-name">
              Firma adı
            </label>
            <input
              id="company-name"
              name="name"
              className="modern-hr-form-input"
              value={companyForm.name}
              onChange={handleCompanyFormChange}
              placeholder="Örn. Kolin Holding"
              required
            />
          </div>
          <div className="modern-hr-form-group mb-3">
            <label className="modern-hr-form-label" htmlFor="company-slug">
              Slug
            </label>
            <input
              id="company-slug"
              name="slug"
              className="modern-hr-form-input"
              value={companyForm.slug}
              onChange={handleSlugChange}
              placeholder="kolin-holding"
            />
          </div>
          <div className="form-check">
            <input
              id="company-active"
              name="isActive"
              type="checkbox"
              className="form-check-input"
              checked={companyForm.isActive}
              onChange={handleCompanyFormChange}
            />
            <label className="form-check-label" htmlFor="company-active">
              Firma aktif
            </label>
          </div>
        </form>
      </ModalShell>

      <ModalShell
        title="Firma Yöneticisi Ata"
        subtitle={assignTargetCompany?.name || ''}
        isOpen={assignModalOpen}
        onClose={closeAssignModal}
        footer={assignModalFooter}
      >
        <p className="text-muted small mb-3">
          E-posta veya ad ile kullanıcı arayın. Seçilen kullanıcı firma yöneticisi rolüne yükseltilir.
        </p>
        <div className="d-flex gap-2 mb-3">
          <input
            className="modern-hr-form-input flex-grow-1"
            value={userSearch}
            onChange={(event) => setUserSearch(event.target.value)}
            placeholder="E-posta veya ad"
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                searchUsers();
              }
            }}
          />
          <button type="button" className="modern-hr-btn modern-hr-btn-primary" onClick={searchUsers} disabled={userSearchLoading}>
            {userSearchLoading ? 'Aranıyor...' : 'Ara'}
          </button>
        </div>

        {userResults.length > 0 && (
          <div className="list-group">
            {userResults.map((user) => (
              <button
                key={user._id}
                type="button"
                className={`list-group-item list-group-item-action ${selectedUser?._id === user._id ? 'active' : ''}`}
                onClick={() => setSelectedUser(user)}
              >
                <div className="fw-semibold">
                  {user.firstName} {user.lastName}
                </div>
                <small>{user.email}</small>
                {user.company ? <small className="d-block text-muted">{user.company}</small> : null}
              </button>
            ))}
          </div>
        )}
      </ModalShell>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}

export default CompanyManagementPage;
